import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ClipboardList, Check, X, Truck, Workflow } from 'lucide-react';

import {
  listOrders,
  setOrderStatus,
  addOrderPayment,
  type SupplierOrder,
  type PurchaseOrderStatus,
  type SupplierOrderAction,
} from '@/services/orders';
import { PageHeader, Spinner, EmptyState } from '@/components/fields';
import { rub, dateTime } from '@/lib/format';
import { useAuthStore } from '@/store/auth.store';
import { OrderModal, StatusBadges } from '@/components/OrderModal';

const FILTERS: { value: PurchaseOrderStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'NEW', label: 'Новые' },
  { value: 'CONFIRMED', label: 'Подтверждённые' },
  { value: 'SHIPPED', label: 'Отгруженные' },
  { value: 'DELIVERED', label: 'Доставленные' },
];

export default function Orders() {
  const qc = useQueryClient();
  const supplier = useAuthStore((s) => s.supplier);
  const [filter, setFilter] = useState<PurchaseOrderStatus | 'all'>('all');
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['orders', filter],
    queryFn: () => listOrders({ status: filter === 'all' ? undefined : filter, limit: 200 }),
    // Список заявок обновляется в реальном времени, как и бейдж на сайдбаре.
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['orders'] });
    qc.invalidateQueries({ queryKey: ['debts'] });
  };

  const status = useMutation({
    mutationFn: ({ id, action }: { id: string; action: SupplierOrderAction }) =>
      setOrderStatus(id, action),
    onSuccess: (_d, v) => {
      invalidate();
      toast.success(
        v.action === 'confirm' ? 'Заказ подтверждён' : v.action === 'reject' ? 'Заказ отклонён' : 'Заказ отгружен',
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const pay = useMutation({
    mutationFn: ({ id, amount, note }: { id: string; amount: number; note?: string }) =>
      addOrderPayment(id, { amount, note }),
    onSuccess: () => {
      invalidate();
      toast.success('Оплата отмечена');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rows = data?.results ?? [];
  const selected = openId ? rows.find((o) => o.id === openId) ?? null : null;
  const erpMode = supplier?.ordersManagedInErp ?? false;

  return (
    <div>
      <PageHeader title="Заявки" subtitle="Входящие заказы от заведений" />

      {erpMode && (
        <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-blue-200 bg-blue-50 p-3.5 text-sm text-blue-900">
          <Workflow size={17} className="mt-0.5 shrink-0" />
          <div>
            Заявки обрабатываются в 1С — подтверждение, отклонение и отгрузка отключены здесь,
            чтобы не задвоить то, что 1С уже сделала через API. Выключить можно в «Интеграциях».
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-1 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={
              'px-3 py-1.5 rounded-lg text-[13px] font-medium transition ' +
              (filter === f.value
                ? 'bg-ink text-white'
                : 'bg-white border border-line text-ink/65 hover:border-ink/40')
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Spinner label="Загружаем заявки…" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ClipboardList size={40} strokeWidth={1.4} />}
          title="Заявок пока нет"
          description="Когда заведение оформит заказ из вашего каталога, он появится здесь."
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {rows.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              busy={status.isPending}
              erpMode={erpMode}
              onOpen={() => setOpenId(o.id)}
              onAction={(action) => status.mutate({ id: o.id, action })}
            />
          ))}
        </div>
      )}

      {selected && (
        <OrderModal
          order={selected}
          supplier={supplier}
          busy={status.isPending || pay.isPending}
          onClose={() => setOpenId(null)}
          onAction={(action) => status.mutate({ id: selected.id, action })}
          onPay={(amount, note) => pay.mutate({ id: selected.id, amount, note })}
        />
      )}
    </div>
  );
}

function OrderCard({
  order,
  busy,
  erpMode,
  onOpen,
  onAction,
}: {
  order: SupplierOrder;
  busy: boolean;
  erpMode: boolean;
  onOpen: () => void;
  onAction: (a: SupplierOrderAction) => void;
}) {
  return (
    <div className="card p-4 flex flex-col gap-3">
      <button onClick={onOpen} className="text-left">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-medium text-ink truncate">{order.establishment.name}</div>
            <div className="text-xs text-ink/45 mt-0.5">{dateTime(order.createdAt)}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-semibold text-ink">{rub(order.totalCost)}</div>
            <div className="text-xs text-ink/45">{order.items.length} поз.</div>
          </div>
        </div>
        <div className="mt-2">
          <StatusBadges order={order} />
        </div>
      </button>

      {!erpMode && (order.status === 'NEW' || order.status === 'CONFIRMED') && (
        <div className="flex gap-2 pt-1">
          {order.status === 'NEW' && (
            <>
              <button
                disabled={busy}
                onClick={() => onAction('confirm')}
                className="btn-primary flex-1 justify-center disabled:opacity-50"
              >
                <Check size={15} /> Подтвердить
              </button>
              <button
                disabled={busy}
                onClick={() => onAction('reject')}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                <X size={15} />
              </button>
            </>
          )}
          {order.status === 'CONFIRMED' && (
            <button
              disabled={busy}
              onClick={() => onAction('ship')}
              className="btn-primary flex-1 justify-center disabled:opacity-50"
            >
              <Truck size={15} /> Отгрузить
            </button>
          )}
        </div>
      )}
    </div>
  );
}
